import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

const ACCENT_COLOR = '#c9a962';
const TEXT_LIGHT = '#ffffff';

const languages = [
  {
    code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr'
  },
  {
    code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl'
  },
  {
    code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr'
  },
];

const styles = {
  container: {
    position: 'relative',
    zIndex: 1000,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(201, 169, 98, 0.15)',
    border: `1px solid ${ACCENT_COLOR}`,
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    color: TEXT_LIGHT,
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    fontWeight: '500',
  },
  buttonHover: {
    backgroundColor: 'rgba(201, 169, 98, 0.25)',
  },
  flag: {
    fontSize: '18px',
  },
  langCode: {
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  arrow: {
    fontSize: '10px',
    transition: 'transform 0.3s ease',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: 'rgba(10, 22, 40, 0.98)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    overflow: 'hidden',
    minWidth: '160px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(16px)',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: 'rgba(255, 255, 255, 0.8)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  dropdownItemLast: {
    borderBottom: 'none',
  },
  dropdownItemHover: {
    backgroundColor: 'rgba(201, 169, 98, 0.15)',
    color: ACCENT_COLOR,
  },
  dropdownItemActive: {
    backgroundColor: 'rgba(201, 169, 98, 0.1)',
    color: ACCENT_COLOR,
  },
  checkmark: {
    marginLeft: 'auto',
    color: ACCENT_COLOR,
    fontSize: '14px',
  },
};

const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [buttonHovered, setButtonHovered] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    const lang = languages.find((l) => l.code === langCode);
    if (lang) {
      document.documentElement.dir = lang.dir;
      document.documentElement.lang = langCode;
    }
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set initial direction based on saved language
  useEffect(() => {
    const lang = languages.find((l) => l.code === i18n.language);
    if (lang) {
      document.documentElement.dir = lang.dir;
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  return (
    <div style={styles.container} ref={dropdownRef}>
      <button
        type="button"
        style={{
          ...styles.button,
          ...(buttonHovered ? styles.buttonHover : {}),
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setButtonHovered(true)}
        onMouseLeave={() => setButtonHovered(false)}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span style={styles.flag}>{currentLanguage.flag}</span>
        <span style={styles.langCode}>{currentLanguage.code}</span>
        <span
          style={{
            ...styles.arrow,
            ...(isOpen ? styles.arrowOpen : {}),
          }}
        >
          ▼
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            style={styles.dropdown}
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {languages.map((lang, index) => (
              <div
                key={lang.code}
                style={{
                  ...styles.dropdownItem,
                  ...(index === languages.length - 1 ? styles.dropdownItemLast : {}),
                  ...(hoveredItem === lang.code ? styles.dropdownItemHover : {}),
                  ...(currentLanguage.code === lang.code ? styles.dropdownItemActive : {}),
                }}
                onClick={() => handleLanguageChange(lang.code)}
                onMouseEnter={() => setHoveredItem(lang.code)}
                onMouseLeave={() => setHoveredItem(null)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleLanguageChange(lang.code);
                  }
                }}
              >
                <span style={styles.flag}>{lang.flag}</span>
                <span>{lang.name}</span>
                {currentLanguage.code === lang.code && (
                  <span style={styles.checkmark}>✓</span>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LanguageSwitcher;
